import { useState } from "react";
import { Heart, CreditCard, Shield, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const presetAmounts = [25, 50, 100, 250, 500];

const impactItems = [
  { amount: "$25", description: "Provides peace education materials for 5 students" },
  { amount: "$50", description: "Supports one community mediation session" },
  { amount: "$100", description: "Funds a youth violence prevention workshop" },
  { amount: "$250", description: "Sponsors a peace advocacy training seminar" },
  { amount: "$500", description: "Enables a full community peace forum event" }
];

export default function Donate() {
  const [donationType, setDonationType] = useState("one-time");
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount(null);
  };

  const getFinalAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    if (selectedAmount) return selectedAmount;
    return 0;
  };

  const handleStripeCheckout = async () => {
    const amount = getFinalAmount();
    if (amount <= 0) {
      toast.error("Please select or enter a donation amount");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount: parseFloat(amount.toFixed(2)),
        donation_type: donationType,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to create checkout session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalDonate = () => {
    // PayPal donate button - opens PayPal giving page
    // For now, we'll show a message about PayPal integration
    toast.info("PayPal donations coming soon! Please use card payment for now.");
  };

  return (
    <div data-testid="donate-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-[#F59E0B] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-6">
              Support Our Mission
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Your donation helps us educate communities, advocate for peace, and transform lives. Every contribution makes a difference.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-6">Make a Donation</h2>

              {/* Donation Type Tabs */}
              <Tabs value={donationType} onValueChange={setDonationType} className="mb-8">
                <TabsList className="grid w-full grid-cols-2 h-12 bg-slate-100 rounded-lg p-1">
                  <TabsTrigger 
                    value="one-time" 
                    className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#0056D2] data-[state=active]:shadow-sm font-medium"
                    data-testid="one-time-tab"
                  >
                    One-Time
                  </TabsTrigger>
                  <TabsTrigger 
                    value="monthly" 
                    className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#0056D2] data-[state=active]:shadow-sm font-medium"
                    data-testid="monthly-tab"
                  >
                    Monthly
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="one-time" className="mt-6">
                  <p className="text-slate-600 text-sm mb-4">Make a one-time contribution to support our programs.</p>
                </TabsContent>
                <TabsContent value="monthly" className="mt-6">
                  <p className="text-slate-600 text-sm mb-4">Become a sustaining donor with a monthly gift.</p>
                </TabsContent>
              </Tabs>

              {/* Amount Selection */}
              <div className="mb-8">
                <Label className="text-sm font-semibold text-[#0F172A] mb-4 block">Select Amount</Label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                  {presetAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountSelect(amount)}
                      className={`amount-btn ${selectedAmount === amount ? "selected" : ""}`}
                      data-testid={`amount-${amount}`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <Input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    className="pl-8 h-12 rounded-lg border-slate-200 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20"
                    min="1"
                    step="0.01"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="space-y-4 mb-8">
                <div>
                  <Label htmlFor="donorName" className="text-sm font-medium text-[#0F172A]">Name (optional)</Label>
                  <Input
                    id="donorName"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1 h-12 rounded-lg"
                    data-testid="donor-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="donorEmail" className="text-sm font-medium text-[#0F172A]">Email (optional)</Label>
                  <Input
                    id="donorEmail"
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-1 h-12 rounded-lg"
                    data-testid="donor-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-[#0F172A]">Message (optional)</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share why you're supporting our mission..."
                    className="mt-1 rounded-lg resize-none"
                    rows={3}
                    data-testid="donor-message-input"
                  />
                </div>
              </div>

              {/* Payment Buttons */}
              <div className="space-y-4">
                <Button
                  onClick={handleStripeCheckout}
                  disabled={loading || getFinalAmount() <= 0}
                  className="w-full btn-donate text-lg h-14"
                  data-testid="stripe-checkout-btn"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Donate ${getFinalAmount() || "0"} with Card
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-slate-500">or</span>
                  </div>
                </div>

                <Button
                  onClick={handlePayPalDonate}
                  variant="outline"
                  className="w-full paypal-btn h-14 justify-center"
                  data-testid="paypal-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                  </svg>
                  Pay with PayPal
                </Button>
              </div>

              {/* Security Note */}
              <div className="flex items-center gap-2 mt-6 text-sm text-slate-500">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Secure payment processing. Your information is protected.</span>
              </div>
            </div>

            {/* Impact Info */}
            <div>
              <div className="bg-[#F0F9FF] rounded-3xl p-8 mb-8">
                <h3 className="text-xl font-bold font-['Outfit'] text-[#0F172A] mb-6">Your Impact</h3>
                <ul className="space-y-4">
                  {impactItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#0056D2] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-[#0056D2]">{item.amount}</span>
                        <span className="text-slate-600 ml-2">{item.description}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-3xl border border-slate-200 p-8">
                <h3 className="text-xl font-bold font-['Outfit'] text-[#0F172A] mb-4">Why Donate?</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Peace for Change Alliance Inc. is a 501(c)(3) non-profit organization. Your donation is tax-deductible to the extent allowed by law.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  100% of your donation goes directly to supporting our programs in:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full"></div>
                    Violence prevention education
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full"></div>
                    Community peace initiatives
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full"></div>
                    Youth mentorship programs
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full"></div>
                    Advocacy and policy work
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
